import { MaskedInput } from 'components/Inputs/MaskedInput';
import { SelectInput } from 'components/Inputs/SelectInput';
import { TextInput } from 'components/Inputs/TextInput';
import { uf } from 'mocks';
import { useEffect, useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { getAddressFromCep } from 'services/viacep-service';
import { unmaskNumber } from 'util/masks';

import {
  CircularProgress,
  Grid,
  InputAdornment,
  MenuItem,
} from '@mui/material';
import { requiredMessage } from 'schemas/validators';
import { useTutorUserRegistration } from 'context';
import { useLoader } from 'hooks/utilHooks';

export const TutorAddressForm: React.FC = () => {
  const {
    loading,
    form: { control },
  } = useTutorUserRegistration();
  const cep = useWatch({ control, name: 'address.cep' });

  const disableInputs = useMemo(() => {
    const unmaskedCep = unmaskNumber(cep);
    if (!unmaskedCep || unmaskedCep?.length < 8) {
      return true;
    }

    return loading;
  }, [cep, loading]);

  return (
    <>
      <Grid item xs={12} md={4}>
        <CepSearchInput />
      </Grid>

      <Grid item xs={12} md={5}>
        <TextInput
          name="address.street"
          id="street"
          label="LOGRADOURO"
          disabled={disableInputs}
          rules={{
            required: requiredMessage('Logradouro'),
          }}
        />
      </Grid>

      <Grid item xs={12} md={3}>
        <TextInput
          name="address.number"
          id="number"
          label="NÚMERO"
          disabled={disableInputs}
          required
          rules={{
            required: requiredMessage('Número'),
          }}
        />
      </Grid>

      <Grid item xs={12} md={4}>
        <TextInput
          name="address.neighborhood"
          id="neighborhood"
          label="BAIRRO"
          disabled={disableInputs}
          required
          rules={{
            required: requiredMessage('Bairro'),
          }}
        />
      </Grid>

      <Grid item xs={12} md={4}>
        <TextInput
          name="address.city"
          id="city"
          label="CIDADE"
          disabled={disableInputs}
          required
          rules={{
            required: requiredMessage('Cidade'),
          }}
        />
      </Grid>

      <Grid item xs={12} md={2}>
        <SelectInput
          name="address.state"
          id="state"
          label="ESTADO"
          disabled={disableInputs}
          required
          rules={{
            required: requiredMessage('Estado'),
          }}
        >
          {Object.entries(uf).map(([value, title]) => (
            <MenuItem key={value} value={value}>
              {title}
            </MenuItem>
          ))}
        </SelectInput>
      </Grid>

      <Grid item xs={12} md={6}>
        <TextInput
          name="address.complement"
          id="complement"
          label="COMPLEMENTO"
          disabled={disableInputs}
        />
      </Grid>
    </>
  );
};

const CepSearchInput: React.FC = () => {
  const {
    loading,
    form: { getValues, control, resetField, setValue, setError, clearErrors },
  } = useTutorUserRegistration();
  const loadingCep = useLoader();
  const cep = useWatch({ control, name: 'address.cep' });

  useEffect(() => {
    // Reset address if CEP changes
    resetField('address.neighborhood');
    resetField('address.street');
    resetField('address.state');
    resetField('address.city');
    resetField('address.number');
  }, [cep, resetField]);

  const handleSearchAddress = async () => {
    const { address } = getValues();

    if (!cep || cep.length < 8) {
      return;
    }

    loadingCep.showLoader();

    try {
      const { city, neighborhood, state, street } = await getAddressFromCep(
        cep
      );
      clearErrors('address.cep');
      setValue('address', {
        ...address,
        city,
        neighborhood,
        state,
        street,
        complement: address?.complement ?? '',
        number: address?.number ?? '',
        cep,
      });
    } catch (reason) {
      const { message } = reason as Error;
      setError('address.cep', { message });
    } finally {
      loadingCep.hideLoader();
    }
  };

  return (
    <MaskedInput
      name="address.cep"
      id="cep"
      label="CEP"
      placeholder="_____-___"
      mask="#####-###"
      onBlur={handleSearchAddress}
      required
      disabled={loadingCep.loading || loading}
      InputProps={{
        endAdornment: loadingCep.loading ? (
          <InputAdornment position="end">
            <CircularProgress size="1.4rem" />
          </InputAdornment>
        ) : null,
      }}
      rules={{
        required: requiredMessage('CEP'),
      }}
    />
  );
};
